import { firstValueFrom, of, Subject } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { fromFetch } from 'rxjs/fetch';
import { HttpRequestHandler, HttpResponseHandler } from '../operators';
import { blobSelector, jsonSelector } from '../selectors';
import { HttpResponseError } from '../../errors/index.js';
import { createSseSelector, } from '../selectors/create-sse-selector';
/** Base http client for executing requests */
export class HttpClient {
    uri;
    /**
     * A request handler that can be used to customize the request before it is sent.
     * This property is part of the `HttpClientCreateOptions` configuration object used to create an `HttpClient` instance.
     */
    requestHandler;
    /**
     * A handler for customizing the response after it is received.
     * This property is part of the `HttpClientCreateOptions` configuration object used to create an `HttpClient` instance.
     */
    responseHandler;
    /**
     * A stream of requests that are about to be executed.
     * This property is used internally by the `HttpClient` class to manage the lifecycle of requests.
     */
    _request$ = new Subject();
    /**
     * A stream of responses that have been received.
     * This property is used internally by the `HttpClient` class to manage the lifecycle of responses.
     */
    _response$ = new Subject();
    /**
     * A stream that is used to signal the abortion of requests.
     * This property is used internally by the `HttpClient` class to manage the lifecycle of requests.
     */
    _abort$ = new Subject();
    /**
     * A stream of requests that are about to be executed.
     * @returns An `Observable` that emits each request before it is executed.
     */
    get request$() {
        return this._request$.asObservable();
    }
    /**
     * A stream of responses that have been received.
     * @returns An `Observable` that emits each response as it is received.
     */
    get response$() {
        return this._response$.asObservable();
    }
    /**
     * Creates a new `HttpClient`.
     * @param uri - The base URI used to resolve relative request paths.
     * @param options - Optional request and response handlers.
     */
    constructor(uri, options) {
        this.uri = uri;
        this.requestHandler = new HttpRequestHandler(options?.requestHandler);
        this.responseHandler = new HttpResponseHandler(options?.responseHandler);
        this._init();
    }
    /**
     * Internal method called by child classes to perform initialization logic in the constructor.
     * This method is intended to be overridden by child classes to add their own initialization logic.
     * @protected
     * @virtual
     */
    _init() {
        // called by children for constructor setup
    }
    /**
     * Fetches data from the specified path and returns a stream response.
     *
     * @template T - The expected shape of the fetched data.
     * @param path - The path to fetch data from.
     * @param args - Optional request initialization options, including a custom selector function.
     * @returns A stream response containing the fetched data.
     */
    fetch$(path, args) {
        return this._fetch$(path, args);
    }
    /**
     * Fetches data from the specified path and returns a Promise containing the fetched data.
     *
     * @template T - The expected shape of the fetched data.
     * @param path - The path to fetch data from.
     * @param args - Optional request initialization options, including a custom selector function.
     * @returns A Promise containing the fetched data.
     */
    fetch(path, args) {
        return firstValueFrom(this.fetch$(path, args));
    }
    /**
     * @deprecated Use {@link fetch} instead.
     * @template T - The expected shape of the fetched data.
     * @param path - The path to fetch data from.
     * @param args - Optional request initialization options, including a custom selector function.
     * @returns A Promise containing the fetched data.
     */
    fetchAsync(path, args) {
        return this.fetch(path, args);
    }
    /**
     * Fetches data from the specified path and returns a stream response containing the data in JSON format.
     *
     * @template T - The expected shape of the parsed JSON data.
     * @param path - The path to fetch the data from.
     * @param args - Optional request initialization options, including a custom selector function and request body.
     *   - `body`: The request body, which will be automatically serialized to JSON if it's an object.
     *   - `selector`: A custom selector function to transform the response data. If not provided, the `jsonSelector` function will be used.
     *   - `headers`: Additional headers to include in the request. The `Accept` and `Content-Type` headers will be automatically set to `application/json`.
     * @returns A stream response containing the fetched data in JSON format.
     */
    json$(path, args) {
        const body = typeof args?.body === 'object' ? JSON.stringify(args?.body) : args?.body;
        const selector = args?.selector ?? jsonSelector;
        const headers = new Headers(args?.headers);
        headers.append('Accept', 'application/json');
        headers.append('Content-Type', 'application/json');
        return this.fetch$(path, {
            ...args,
            body,
            selector,
            headers,
        });
    }
    /**
     * Fetches data from the specified path and returns a Promise containing the fetched data in JSON format.
     *
     * @template T - The expected shape of the parsed JSON data.
     * @param path - The path to fetch the data from.
     * @param args - Optional request initialization options, including a custom selector function and request body.
     *   - `body`: The request body, which will be automatically serialized to JSON if it's an object.
     *   - `selector`: A custom selector function to transform the response data. If not provided, the `jsonSelector` function will be used.
     *   - `headers`: Additional headers to include in the request. The `Accept` and `Content-Type` headers will be automatically set to `application/json`.
     * @returns A Promise containing the fetched data in JSON format.
     */
    json(path, args) {
        return firstValueFrom(this.json$(path, args));
    }
    /**
     * Fetches a blob resource from the specified path and returns a stream response.
     *
     * @template T - The expected shape of the fetched blob data.
     * @param path - The path to the blob resource.
     * @param args - Optional request initialization options, including a custom selector function.
     * @returns A stream response containing the fetched blob data.
     */
    blob$(path, args) {
        // Get the selector value from the provided args, or use the default blobSelector
        const selector = args?.selector ?? blobSelector;
        // Create the FetchRequestInit object with the provided args and the selector
        const init = {
            ...args,
            selector,
        };
        // Call the fetch$ method with the provided path and the constructed init object
        return this.fetch$(path, init);
    }
    /**
     * Fetches a blob from the specified path and returns a Promise that resolves to the blob result.
     *
     * @template T - The expected shape of the fetched blob data.
     * @param path - The path to fetch the blob from.
     * @param args - Optional arguments for the fetch request, including request body, headers, and response type.
     * @returns A Promise that resolves to the blob result.
     */
    blob(path, args) {
        return firstValueFrom(this.blob$(path, args));
    }
    /**
     * Initiates a Server-Sent Events (SSE) stream request to the specified path and returns a stream of events.
     *
     * @template T - The type of the event data expected from the SSE stream.
     * @param path - The endpoint path to connect to for the SSE stream.
     * @param args - Optional fetch request initialization options, including headers and abort signal.
     * @param options - Optional selector options for customizing the SSE stream, excluding the abort signal.
     *
     * @returns A `StreamResponse` that emits `ServerSentEvent<T>` objects as they are received from the server.
     *
     * @example
     * const sse$ = httpClient.sse$(
     *   '/events',
     *   { method: 'POST', body: JSON.stringify({ prompt: 'tell me a joke' }) },
     *   { eventFilter: ['message'] },
     * );
     * sse$.subscribe({
     *   next: (event) => console.log(event),
     *   error: (err) => console.error(err),
     *   complete: () => console.log('Completed'),
     * });
     */
    sse$(path, args, options) {
        // Setup default common headers for SSE
        const headers = new Headers(args?.headers);
        headers.append('Accept', 'text/event-stream');
        headers.append('Content-Type', 'text/event-stream');
        headers.append('Cache-Control', 'no-cache');
        headers.append('Connection', 'keep-alive');
        // Create the selector using the provided options and the abort signal from args
        const selector = createSseSelector({ ...options, abortSignal: args?.signal });
        // Call the fetch$ method with the provided path and the constructed init object
        return this._fetch$(path, { selector, ...args, headers });
    }
    /**
     * @deprecated Use {@link json} instead.
     * @template T - The expected shape of the parsed JSON data.
     * @param path - The path to fetch the data from.
     * @param args - Optional request initialization options, including a custom selector function and request body.
     * @returns A Promise containing the fetched data in JSON format.
     */
    jsonAsync(path, args) {
        return this.json(path, args);
    }
    /**
     * Executes an HTTP request using the specified method and path.
     *
     * @template T - The expected shape of the result.
     * @template TMethod - The name of the `IHttpClient` method to invoke.
     * @param method - The HTTP method to use for the request, such as 'fetch', 'json', or 'blob'.
     * @param path - The path to the resource to fetch.
     * @param init - Optional request initialization options, including request body, headers, and response type.
     * @returns The result of the HTTP request, which will be of the same type as the return value of the specified method.
     */
    execute(method, path, init) {
        return this[method](path, init);
    }
    /**
     * Aborts any ongoing HTTP requests made by this `IHttpClient` instance.
     * This will trigger the `takeUntil` operator in the `_fetch$` method,
     * causing any in-flight requests to be cancelled.
     */
    abort() {
        this._abort$.next();
    }
    /**
     * Fetches data from the specified path and returns an Observable that emits the response.
     *
     * @template T - The expected shape of the emitted data.
     * @param path - The path to fetch the data from.
     * @param args - Optional arguments for the fetch request, including a response selector function, request body, headers, and response type.
     * @returns {Observable<T>} An Observable that emits the response data.
     *
     * @throws {HttpResponseError} When the optional `selector` throws while transforming the response.
     *
     * This method handles the following steps:
     * 1. Resolves the full URL by combining the base URI and the provided path.
     * 2. Prepares the request by passing it through the `requestHandler.process()` method.
     * 3. Executes the fetch request using the prepared request.
     * 4. Prepares the response by passing it through the `responseHandler.process()` method.
     * 5. Applies the optional response selector function to transform the response data.
     * 6. Cancels the request if the `_abort$` observable emits.
     */
    _fetch$(path, args) {
        const { selector, ...options } = args || {};
        // `fromFetch` yields the raw fetch `Response`, but `responseHandler.process()` (called via
        // `_prepareResponse`) expects the pipeline's generic `TResponse` shape — cast through
        // `unknown` since the two are only compatible after that processing step.
        const response$ = of({
            ...options,
            path,
            uri: this._resolveUrl(path),
        }).pipe(
        /** prepare request, allow extensions to modify request  */
        switchMap((x) => this._prepareRequest(x)), 
        /** push request to event buss */
        tap((x) => this._request$.next(x)), 
        /** execute request */
        switchMap(({ uri, path: _path, ...init }) => fromFetch(uri, init)), 
        /** prepare response, allow extensions to modify response  */
        switchMap((x) => this._prepareResponse(x)), 
        /** push response to event buss */
        tap((x) => this._response$.next(x)), 
        /** execute selector */
        switchMap((response) => {
            // only run the selector when one was provided; otherwise pass the response through untouched
            if (selector) {
                try {
                    return selector(response);
                }
                catch (err) {
                    throw new HttpResponseError('failed to execute response selector', response, {
                        cause: err,
                    });
                }
            }
            return of(response);
        }), 
        /** cancel request on abort signal */
        takeUntil(this._abort$));
        // The pipe above resolves to the per-call generic `T` (via the optional `selector`), but
        // the observable's static type tracks the class-level `TResponse` — cast to the caller's `T`.
        return response$;
    }
    /**
     * Prepares the request by passing it through the `requestHandler.process()` method.
     * This method is an implementation detail of the `_fetch$()` method, and is not part of the public API.
     * It takes a `TRequest` object as input, which represents the request data, and returns an `ObservableInput<TRequest>`,
     * which can be used to further process the request before it is executed.
     *
     * @param init The request data to be processed.
     * @returns An `ObservableInput<TRequest>` that represents the processed request.
     */
    _prepareRequest(init) {
        return this.requestHandler.process(init);
    }
    /**
     * Prepares the response by passing it through the `responseHandler.process()` method.
     * This method is an implementation detail of the `_fetch$()` method, and is not part of the public API.
     * It takes a `TResponse` object as input, which represents the response data, and returns an `ObservableInput<TResponse>`,
     * which can be used to further process the response before it is returned.
     *
     * @param response The response data to be processed.
     * @returns An `ObservableInput<TResponse>` that represents the processed response.
     */
    _prepareResponse(response) {
        return this.responseHandler.process(response);
    }
    /**
     * Resolves the full URL for a given path by combining it with the base URL.
     * This is a protected method and is an implementation detail of the `HttpClient` class.
     *
     * @param path - The path to be resolved.
     * @returns The full URL for the given path.
     */
    _resolveUrl(path) {
        const { origin, pathname: basePath } = new URL(this.uri, this.uri.startsWith('http') ? undefined : window.location.origin);
        const pathname = [basePath, path].join('/').replace(/\/{2,}/g, '/');
        return new URL(pathname, origin).href;
    }
}
//# sourceMappingURL=client.js.map